import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import CategorySelect from "./category-select";
import { emptySchedule, QuickReply, QuickReplyCategory } from "./types";

const AddQuickReplyModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  categories: QuickReplyCategory[];
  defaultAddedBy?: string;
  onAdd: (reply: QuickReply) => void;
}> = ({ isOpen, onClose, categories, defaultAddedBy, onAdd }) => {
  const [categoryValue, setCategoryValue] = useState(categories[0]?.value || "");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (isOpen) {
      setCategoryValue(categories[0]?.value || "");
      setMessage("");
    }
  }, [isOpen, categories]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!message.trim()) {
      toast.error("Enter a quick reply message");
      return;
    }
    onAdd({
      id: `${Date.now()}`,
      categoryValue,
      message: message.trim(),
      addedBy: defaultAddedBy || "You",
      createdOn: new Date().toISOString(),
      status: "Active",
      schedule: emptySchedule(),
    });
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="flex items-start justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add a Quick Reply</h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400">
              <X size={20} />
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Add pre-defined replies to quickly send to your customers</p>

          <div className="space-y-4">
            <CategorySelect label="Reply Category" categories={categories} value={categoryValue} onChange={setCategoryValue} />

            <div>
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Enter Quick Reply</label>
              <textarea
                value={message}
                maxLength={400}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter Quick Reply"
                rows={4}
                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-transparent text-sm text-gray-700 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-4 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/30"
              />
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">{message.length}/ 400</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 mt-6">
            <button
              onClick={handleSave}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Save Quick Reply
            </button>
            <button
              onClick={onClose}
              className="w-full py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddQuickReplyModal;
